import React, {
    useCallback, forwardRef
} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import Card from 'bdvp-common/src/containers/Card';
import { getMediaPath } from 'bdvp-common/src/utils';
import { Tracking } from 'bdvp-tracking';
import {
    useLang,
    useBasePath,
    useDictionary,
    useMediaConfig,
} from 'bdvp-common/src/hooks/config';
import { MEDIA_CONFIG_CONTEXT } from 'bdvp-common/src/utils/constants';

import Carousel, { imgSizes } from './Carousel';
import Title from './Title';

export const Wrapper = styled.div`
    margin-bottom: 30px;
`;

const Row = forwardRef(({
    title,
    list,
    hasMore,
    error,
    fetchMoreData,
    isLoading,
    isLoadingMore,
    isSearchData,
    limit,
    link,
    searchParams,
    rank,
}, ref) => {

    const history = useHistory();
    const config = useMediaConfig(MEDIA_CONFIG_CONTEXT.home);
    const lang = useLang();
    const basePath = useBasePath();
    const dictionary = useDictionary();

    const onItemClickHandler = useCallback((event, item, index) => {

        event.preventDefault();
        const mediaPath = getMediaPath(item);
        const search = isSearchData ? `${searchParams}&p=${Math.floor(index / limit)}` : searchParams;
        history.push({
            pathname: `/${mediaPath}`,
            search,
        });

        Tracking.trackCarouselOnItemClick({
            carouselRank: rank,
            carouselTitle: title,
            videoRank: index,
            videoSlug: item.slug,
        });

    }, [
        history,
        isSearchData,
        limit,
        searchParams,
        rank,
        title,
    ]);

    const onAfterChangeHandler = useCallback((isScrollLeft) => {

        Tracking.trackCarouselScroll(rank, title, isScrollLeft);

    }, [
        rank,
        title,
    ]);

    const renderItem = useCallback((item, isLoading, index = 0) => (
        <Card
            isLoading={isLoading}
            basePath={basePath}
            config={config}
            dictionary={dictionary}
            lang={lang}
            mediaPlayback={item}
            onClick={(e) => onItemClickHandler(e, item, index)}
            orientation={'portrait'}
            searchParams={searchParams}
            preventOverflow={false}
            popoverPlacement={'top-end'}
            imgSizes={imgSizes}
        />
    ), [
        basePath,
        config,
        dictionary,
        lang,
        searchParams,
        onItemClickHandler,
    ]);

    return (
        <Wrapper ref={ref}>
            <Title
                isLoading={isLoading}
                title={title}
                link={link}
                rank={rank}
            />
            <Carousel
                hasMore={hasMore}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                limit={limit}
                list={list}
                fetchMoreData={fetchMoreData}
                renderItem={renderItem}
                onAfterChange={onAfterChangeHandler}
            />
        </Wrapper>
    );

});

Row.propTypes = {
    fetchMoreData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    isSearchData: PropTypes.bool.isRequired,
    limit: PropTypes.number.isRequired,
    link: PropTypes.string.isRequired,
    rank: PropTypes.number.isRequired,
    data: PropTypes.object,
    error: PropTypes.object,
    searchParams: PropTypes.string,
};

Row.defaultProps = {
    data: {},
    error: undefined,
    searchParams: '',
};

export default Row;
